import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AdminService } from 'src/app/core/admin.service';
import Booking, { BookingStatus } from 'src/app/data/Booking';
import { SubscriberComponent } from '../../shared/subscriber.component';
import { MatSort, MatSortable, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { BookingService } from 'src/app/core/booking.service';
import ServiceInfo from 'src/app/data/ServiceInfo';
import BookingRequest from 'src/app/data/BookingRequest';
import { Inject, LOCALE_ID } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Component({
    selector: 'app-bookings',
    templateUrl: './bookings.component.html',
    styleUrls: ['./bookings.component.scss'],
    standalone: false
})
export class BookingsComponent extends SubscriberComponent implements OnInit {

  bookingsTableColumns = ['date', 'name', 'email', 'location', 'status', 'id', 'errors'];
  mobileBookingsTableColumns = ['mobileData'];
  bookingStatuses = Object.values(BookingStatus);

  @ViewChild(MatSort) sort: MatSort;
  dataSource = new MatTableDataSource<Booking>([]);

  hidePast = true;
  selectedStatuses: BookingStatus[] = Object.values(BookingStatus).filter(s => s !== BookingStatus.CANCELED);

  selectedBooking: Booking | null;
  unhandledRequests: string[] | null;

  showAddForm = false;
  addForm: UntypedFormGroup;
  services: ServiceInfo[] | null = null;
  defaultEventHours = 19;
  searchTerm = '';
  searchTermValue = '';

  isMobile = false;
  mobileSortValue = 'date-asc';

  constructor(
    private adminService: AdminService,
    private bookingService: BookingService,
    private formBuilder: UntypedFormBuilder,
    private breakpointObserver: BreakpointObserver,
    @Inject(LOCALE_ID) public locale: string,
    private activeRoute: ActivatedRoute) {
    super();

    this.addForm = this.formBuilder.group({
      service: [null, Validators.required],
      dateTime: [null, Validators.required],
      location: ['', Validators.required],
      message: [''],
      customer: this.formBuilder.group({
        name: ['', Validators.required],
        email: ['', Validators.required],
        phone: ['', Validators.required]
      })
    });
  }

  ngOnInit(): void {
    this.breakpointObserver.observe([
      Breakpoints.XSmall,
      Breakpoints.Small,
      // Breakpoints.Handset
    ]).pipe(takeUntil(this.ngUnsubscribe)).subscribe(result => {
      this.isMobile = result.matches;
    });

    this.bookingService.getServices()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(services => {
        this.services = services;
        if (this.services != null && this.services.length === 1) {
          this.addForm.controls.service.setValue(this.services[0]);
        }
      });
    this.dataSource.sortingDataAccessor = (item: Booking, property: string) => {
      switch (property) {
        case 'date': return (item.dateTime || item.request.dateTime).getTime();
        case 'name': return item.request.customer.name;
        case 'email': return item.request.customer.email;
        case 'location': return item.location || item.request.location;
        case 'status': return item.status;
        default: return (item as any)[property];
      }
    };

    this.dataSource.filterPredicate = (data: Booking, filter: string) => {
      // 1. Text search filter - takes precedence
      if (this.searchTerm) {
        const dataStr = (
          data.request.customer.name + ' ' +
          data.request.customer.email + ' ' +
          (data.location || data.request.location) + ' ' +
          data.id
        ).toLowerCase();
        
        return dataStr.indexOf(this.searchTerm) !== -1;
      }

      // 2. Date filter
      if (this.hidePast) {
        const date = data.dateTime || data.request.dateTime;
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        if (date < now) {
          return false;
        }
      }

      // 3. Status filter
      if (!this.selectedStatuses.includes(data.status)) {
        return false;
      }

      return true;
    };

    combineLatest([this.adminService.getBookings(), this.activeRoute.paramMap])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([bookings, paramMap]) => {
        if (bookings) {
          this.dataSource.data = bookings;
          // Trigger filter initial state
          this.applyFilter();
          
          if (!this.dataSource.sort && this.sort) {
            this.dataSource.sort = this.sort;
            this.sort.sort({ id: 'date', start: 'asc', disableClear: false });
          }
        }

        const bookingId = paramMap?.get('id');
        if (bookings != null && bookingId != null && bookingId !== 'all') {
          const booking = bookings.find(b => b.id === bookingId);
          this.selectedBooking = booking;
        } else {
          this.selectedBooking = null;
        }
      });

    this.adminService.getUnhandledRequests()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(ids => this.unhandledRequests = ids);
  }

  applyFilter() {
    this.dataSource.filter = '' + Math.random();
  }

  applySearch(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchTerm = filterValue.trim().toLowerCase();
    this.applyFilter();
  }

  clearSearch() {
    this.searchTermValue = '';
    this.searchTerm = '';
    this.applyFilter();
  }

  onMobileSortChange() {
    const [id, start] = this.mobileSortValue.split('-');
    this.sort.sort({ id, start: start as 'asc' | 'desc', disableClear: false });
  }

  onAddSubmit() {
    if (this.addForm.valid) {
      const request = new BookingRequest('et'); // Default to 'et' for admin entry
      Object.assign(request, this.addForm.value);
      request.adminEntry = true;
      
      // Default start time for calendar events
      request.dateTime = new Date(request.dateTime);
      request.dateTime.setHours(this.defaultEventHours);
      request.dateTime.setMinutes(0);

      this.addForm.disable();
      this.bookingService.createBookingRequest(request)
        .then(() => {
          this.showAddForm = false;
          this.addForm.enable();
          this.addForm.reset();
        }).catch(error => {
          console.error(error);
          this.addForm.enable();
        });
    }
  }

}
